import { useMemo, useState } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faMagnifyingGlass, faXmark } from "@fortawesome/pro-regular-svg-icons"

import { StandardChip } from "../../../../../../../shared/components/badges/StandardChip"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"

import { CompanyMerchantPriceLists } from "../../companyPriceListsApi"
import { CompanyUserFormValues } from "../../companyUserFormTypes"

type CatalogAccessTabProps = Readonly<{
    merchants: ReadonlyArray<CompanyMerchantPriceLists>
    isLoading: boolean
}>

const useStyles = makeStyles()((theme) => ({
    loading: {
        display: "flex",
        justifyContent: "center",
        padding: "2.5rem 0",
    },
    header: {
        display: "flex",
        flexDirection: "column",
        gap: ".25rem",
    },
    selectedCard: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        backgroundColor: theme.palette.tailwind.slate[50],
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".75rem",
        padding: ".75rem",
    },
    selectedGroup: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
    },
    groupLabel: {
        color: theme.palette.tailwind.slate[500],
        fontWeight: 700,
        fontSize: ".75rem",
    },
    chipRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: ".5rem",
    },
    chip: {
        "&.MuiChip-root": {
            height: "auto",
            border: "none",
            borderRadius: ".5rem",
        },
        "&.MuiChip-root .MuiChip-label": {
            padding: ".25rem .5rem .25rem .75rem",
            fontSize: ".8125rem",
            fontWeight: 600,
        },
        "&.MuiChip-root .MuiChip-deleteIcon": {
            margin: "0 .5rem 0 0",
            color: theme.palette.tailwind.indigo[400],
            "&:hover": {
                color: theme.palette.tailwind.indigo[600],
            },
        },
    },
    accordion: {
        borderRadius: ".75rem",
        boxShadow: "none",
        overflow: "hidden",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        "&:first-of-type, &:last-of-type": {
            borderRadius: ".75rem",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
        "&:before": {
            display: "none",
        },
    },
    summary: {
        minHeight: "3.5rem",
        backgroundColor: theme.palette.tailwind.slate[50],
        "&.Mui-expanded": {
            minHeight: "3.5rem",
            borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        },
        "& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded": {
            margin: 0,
            alignItems: "center",
        },
        "& .MuiAccordionSummary-expandIconWrapper": {
            color: theme.palette.tailwind.slate[500],
        },
    },
    summaryContent: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        width: "100%",
    },
    merchantName: {
        flexGrow: 1,
        fontWeight: 600,
    },
    details: {
        display: "flex",
        flexDirection: "column",
        gap: ".25rem",
        padding: "1rem",
    },
    row: {
        alignItems: "center",
        width: "100%",
        margin: 0,
        padding: ".5rem",
    },
    empty: {
        padding: "1rem 0",
    },
}))

export function CatalogAccessTab({ merchants, isLoading }: CatalogAccessTabProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { values, setFieldValue } = useFormikContext<CompanyUserFormValues>()

    const [search, setSearch] = useState("")
    const [expanded, setExpanded] = useState<ReadonlySet<number>>(() => new Set())

    const selected = values.assignedPriceListIds
    const selectedSet = useMemo(() => new Set(selected), [selected])

    const setSelected = (next: ReadonlyArray<number>) => setFieldValue("assignedPriceListIds", next)

    const toggleOne = (id: number) =>
        setSelected(selectedSet.has(id) ? selected.filter((value) => value !== id) : [...selected, id])

    const toggleMerchant = (merchant: CompanyMerchantPriceLists) => {
        const ids = merchant.priceLists.map((priceList) => priceList.id)
        const allSelected = ids.length > 0 && ids.every((id) => selectedSet.has(id))
        if (allSelected) {
            const removed = new Set(ids)
            setSelected(selected.filter((id) => !removed.has(id)))
        } else {
            const merged = new Set(selected)
            ids.forEach((id) => merged.add(id))
            setSelected([...merged])
        }
    }

    const toggleExpanded = (merchantId: number) =>
        setExpanded((prev) => {
            const next = new Set(prev)
            if (next.has(merchantId)) next.delete(merchantId)
            else next.add(merchantId)
            return next
        })

    // Selected price lists resolved against the loaded merchants, grouped by canteen — powers the summary
    // line and the removable-chips card.
    const selectedGroups = useMemo(
        () =>
            merchants
                .map((merchant) => ({
                    merchant,
                    priceLists: merchant.priceLists.filter((priceList) => selectedSet.has(priceList.id)),
                }))
                .filter((group) => group.priceLists.length > 0),
        [merchants, selectedSet],
    )
    const selectedCount = selectedGroups.reduce((total, group) => total + group.priceLists.length, 0)
    const selectedNames = selectedGroups.flatMap((group) => group.priceLists.map((priceList) => priceList.displayName))

    const filteredMerchants = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return merchants
        return merchants.filter(
            (merchant) =>
                merchant.displayName.toLowerCase().includes(query) ||
                merchant.priceLists.some((priceList) => priceList.displayName.toLowerCase().includes(query)),
        )
    }, [merchants, search])

    if (isLoading) {
        return (
            <Box className={classes.loading}>
                <LoadingSpinner />
            </Box>
        )
    }

    return (
        <Stack direction="column" spacing="1.5rem">
            <Box className={classes.header}>
                <LocalizedStrict id="company-user-catalog-description">
                    <Typography variant="body2" color="text.secondary">
                        A user can access one or more canteens; expand a canteen to pick its pricelists.
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict id="company-user-catalog-heading">
                    <Typography variant="subtitle2">Delegated canteens / pricelists</Typography>
                </LocalizedStrict>
                {selectedCount === 0 ? (
                    <LocalizedStrict id="company-user-catalog-summary-empty">
                        <Typography variant="body2" color="text.secondary">
                            No canteens / pricelists selected
                        </Typography>
                    </LocalizedStrict>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {selectedNames.join(", ")} ·{" "}
                        {l10n.getString("company-user-catalog-summary-counts", {
                            count: selectedCount,
                            canteens: selectedGroups.length,
                        })}
                    </Typography>
                )}
            </Box>

            {selectedCount > 0 && (
                <Box className={classes.selectedCard}>
                    {selectedGroups.map((group) => (
                        <Box key={group.merchant.id} className={classes.selectedGroup}>
                            <Box component="span" className={classes.groupLabel}>
                                {group.merchant.displayName}
                            </Box>
                            <Box className={classes.chipRow}>
                                {group.priceLists.map((priceList) => (
                                    <StandardChip
                                        key={priceList.id}
                                        variant="purple"
                                        size="small"
                                        className={classes.chip}
                                        onDelete={() => toggleOne(priceList.id)}
                                        deleteIcon={<FontAwesomeIcon icon={faXmark} />}
                                    >
                                        {priceList.displayName}
                                    </StandardChip>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            <TextField
                size="small"
                fullWidth
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={l10n.getString("company-user-catalog-search-placeholder")}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </InputAdornment>
                    ),
                }}
            />

            {filteredMerchants.length === 0 ? (
                <LocalizedStrict id="company-user-catalog-empty">
                    <Typography variant="body2" color="text.secondary" className={classes.empty}>
                        No canteens found.
                    </Typography>
                </LocalizedStrict>
            ) : (
                <Stack direction="column" spacing=".75rem">
                    {filteredMerchants.map((merchant) => {
                        const ids = merchant.priceLists.map((priceList) => priceList.id)
                        const selectedInMerchant = ids.filter((id) => selectedSet.has(id)).length
                        const allSelected = selectedInMerchant > 0 && selectedInMerchant === ids.length
                        const someSelected = selectedInMerchant > 0 && !allSelected

                        return (
                            <Accordion
                                key={merchant.id}
                                expanded={expanded.has(merchant.id)}
                                onChange={() => toggleExpanded(merchant.id)}
                                className={classes.accordion}
                            >
                                <AccordionSummary
                                    className={classes.summary}
                                    expandIcon={<FontAwesomeIcon icon={faChevronDown} />}
                                >
                                    <Box className={classes.summaryContent}>
                                        <Checkbox
                                            checked={allSelected}
                                            indeterminate={someSelected}
                                            onClick={(event) => event.stopPropagation()}
                                            onChange={() => toggleMerchant(merchant)}
                                        />
                                        <Typography variant="subtitle1" className={classes.merchantName}>
                                            {merchant.displayName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedInMerchant}/{ids.length}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails className={classes.details}>
                                    {merchant.priceLists.map((priceList) => (
                                        <FormControlLabel
                                            key={priceList.id}
                                            className={classes.row}
                                            control={
                                                <Checkbox
                                                    checked={selectedSet.has(priceList.id)}
                                                    onChange={() => toggleOne(priceList.id)}
                                                />
                                            }
                                            label={<Typography variant="body1">{priceList.displayName}</Typography>}
                                        />
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        )
                    })}
                </Stack>
            )}
        </Stack>
    )
}
